// ====================================================================================
// Component: OverviewPage (v5.5 - Data Upload Drawer)
// Orchestrator: imports all self-contained containers from the OverviewPage folder.
// MODIFIED: Added `DataUploadDrawer` — right slide-in drawer triggered by inline
//           "Upload Data" button in the Campaign Overview header row.
// ====================================================================================
import { useState, useEffect } from "react";
import { Loader2, AlertTriangle, Upload } from "lucide-react";

// --- Container Imports ---
import ElectionPredictionSummary from './ElectionPredictionSummary';
import KPILinks from './KPILinks';
import ImageUploadDialog from './ImageUploadDialog';
import SurveyDialog from './SurveyDialog';
import CurrentCampaigns from './CurrentCampaigns';
import SwingAnalysisSummary from './SwingAnalysisSummary';
import ArchetypeSynthesis from './ArchetypeSynthesis';
import CandidateMinervaBriefing from './CandidateMinervaBriefing';
import CampaignPsychographicBriefing from './CampaignPsychographicBriefing';
import DataUploadDrawer from './DataUploadDrawer';
export { ArchetypeDetailDrawer } from './ArchetypeDetailDrawer';

// ====================================================================================
// HELPER: DataContainer (for Loading/Error states)
// ====================================================================================
const DataContainer = ({ isLoading, error, children }) => {
  if (isLoading) {
    return (
      <div className="panel p-6 flex items-center justify-center h-64 bg-slate-800/50">
        <Loader2 className="animate-spin text-slate-400" size={24} />
        <span className="ml-2 text-slate-400">Loading Strategic Data...</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className="panel p-6 flex flex-col items-center justify-center h-64 bg-red-900/20 border border-red-700">
        <AlertTriangle className="text-danger-400" size={32} />
        <h3 className="text-white font-semibold mt-2">Error Loading Data</h3>
        <p className="text-slate-400 text-sm">{error.message}</p>
      </div>
    );
  }
  return children;
};

// ====================================================================================
// EXPORT: The Main OverviewPage Component
// ====================================================================================
export const OverviewPage = ({ overviewData, setActiveView, setDrawerData }) => {
  const [predictionData, setPredictionData]   = useState(null);
  const [archetypeData, setArchetypeData]     = useState(null);
  const [swingData, setSwingData]             = useState(null);
  const [campaignDetails, setCampaignDetails] = useState(null);
  const [candidateProfile, setCandidateProfile] = useState(null);
  const [isLoading, setIsLoading]             = useState(true);
  const [error, setError]                     = useState(null);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [showSurveyDialog, setShowSurveyDialog] = useState(false);
  const [showUploadDrawer, setShowUploadDrawer] = useState(false);

  // --- Data Fetching Logic ---
  useEffect(() => {
    console.log(overviewData);
    if (!overviewData) {
      setError(new Error("No campaign selected."));
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    setPredictionData(null);
    setArchetypeData(null);
    setSwingData(null);
    setCampaignDetails(null);
    setCandidateProfile(null);

    setCampaignDetails(overviewData.campaignDetails);
    setPredictionData(overviewData.predictionData);
    setArchetypeData(overviewData.archetypeData);
    setSwingData(overviewData.swingData);
    setCandidateProfile(overviewData.profile);
    setIsLoading(false);

  }, [overviewData]);

  const LOGGED_IN_USER_PARTY = campaignDetails?.party;

  return (
    <div className="p-4 sm:p-8 space-y-8 relative">

      {/* RENDER THE DIALOGS */}
      <ImageUploadDialog isOpen={showImageUpload} onClose={() => setShowImageUpload(false)} />
      {campaignDetails && (
        <SurveyDialog
          isOpen={showSurveyDialog}
          onClose={() => setShowSurveyDialog(false)}
          surveyData={campaignDetails}
        />
      )}

      {/* DATA UPLOAD DRAWER — slide in from right */}
      <DataUploadDrawer
        isOpen={showUploadDrawer}
        onClose={() => setShowUploadDrawer(false)}
        campaignId={campaignDetails?.campaignID}
        partyName={LOGGED_IN_USER_PARTY}
      />

      {/* ── Campaign Overview Header with inline Upload Data button ── */}
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1">Campaign Overview</h1>
          <p className="text-slate-400">
            High-level insights for Campaign: {overviewData?.campaignName ?? "No Active Campaign"}
          </p>
        </div>

        <button
          onClick={() => setShowUploadDrawer(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm text-slate-300 hover:text-white hover:bg-slate-700 hover:border-slate-600 transition-colors"
        >
          <Upload size={15} />
          Upload Data
        </button>
      </div>

      {/* 1. Election Prediction Summary */}
      <DataContainer isLoading={isLoading} error={error}>
        <ElectionPredictionSummary
          predictionData={predictionData}
          swingData={swingData}
          setDrawerData={setDrawerData}
          loggedInUserParty={LOGGED_IN_USER_PARTY}
        />
      </DataContainer>

      {/* 2. KPI Action Bar */}
      <KPILinks
        setActiveView={setActiveView}
        setShowImageUpload={setShowImageUpload}
        setShowSurveyDialog={setShowSurveyDialog}
      />

      {/* 3. Current Campaigns */}
      <CurrentCampaigns
        campaignId={campaignDetails?.campaignID}
        setActiveView={setActiveView}
      />

      {/* 4. CORE STRATEGIC COLUMNS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

        {/* LEFT COLUMN: Swing Analysis (2/3 width) */}
        <div className="lg:col-span-2 min-w-0">
          <DataContainer isLoading={isLoading} error={error}>
            {swingData && (
              <SwingAnalysisSummary swingData={swingData} />
            )}
          </DataContainer>

          <br />

          {/* 5. CANDIDATE MINERVA BRIEFING */}
          <DataContainer isLoading={isLoading} error={error}>
            <CandidateMinervaBriefing profileData={candidateProfile} />
          </DataContainer>
        </div>

        {/* RIGHT COLUMN: Archetype Synthesis (1/3 width) */}
        <div className="lg:col-span-1 min-w-0">
          <DataContainer isLoading={isLoading} error={error}>
            {archetypeData && (
              <ArchetypeSynthesis
                archetypeData={archetypeData}
                setDrawerData={setDrawerData}
              />
            )}
          </DataContainer>
        </div>
      </div>

      {/* 6. PSYCHOGRAPHIC BRIEFING SECTION */}
      <DataContainer isLoading={isLoading} error={error}>
        {swingData && (
          <CampaignPsychographicBriefing swingData={swingData} />
        )}
      </DataContainer>

    </div>
  );
};